import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import TableWrapper from "./TableWrapper";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  InboxIcon,
} from "@heroicons/react/outline";
import LoadErrorState from "../LoadErrorState";

// Cells that stack several sub-rows (e.g. one journal line per account) need
// their dividers to bleed through the td's px-6 so neighbouring columns line up
// into a single rule instead of three floating stubs. Kept next to the cell
// padding below so the two stay in sync.
export const JOURNAL_STACK_CLASS = "flex flex-col divide-y divide-gray-200";
export const JOURNAL_ROW_CLASS = "py-1.5 -mx-6 px-6";

// Set `divider: true` on a column to rule off the group it starts — e.g. where
// per-transaction fields end and per-journal-line fields begin. Applied to the
// header cell too so the line runs the full height of the table.
const DIVIDER_CLASS = "border-l border-gray-200";

const EmptyState = () => {
  const { t } = useTranslation();

  return (
    <div className="text-center py-12">
      <InboxIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-2 text-sm font-medium text-gray-900">
        {t("common.noData")}
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        {t("table.messages.creates.data")}
      </p>
    </div>
  );
};

const Table = ({
  columns = [],
  data = [],
  isLoading = false,
  error = null,
  onRetry,
  rowClassName,
  // Opens a row's detail on a click anywhere in it. A control inside a cell
  // that does something else must stop its click from reaching the row.
  onRowClick,
  // One cell per column (null leaves it blank), rendered as a totals row in
  // <tfoot>. Each cell takes its column's `align` and `className`, so a total
  // lines up under the figures it sums. Shown only when there are rows.
  footer,
}) => {
  const { t } = useTranslation();

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  if (columns.length === 0) return null;

  if (error) {
    return (
      <TableWrapper>
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, i) => (
              <th
                key={i}
                className={`px-6 py-3 text-${
                  column.align || "left"
                } text-xs font-medium text-gray-500 uppercase tracking-wider`}
              >
                {t(column.header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={columns.length}>
              <LoadErrorState error={error} onRetry={onRetry} />
            </td>
          </tr>
        </tbody>
      </TableWrapper>
    );
  }

  if (isLoading) {
    return (
      <TableWrapper>
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, i) => (
              <th key={i} className="px-6 py-3">
                <div className="h-4 bg-gray-200 rounded animate-pulse" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {[...Array(5)].map((_, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((_, colIndex) => (
                <td key={colIndex} className="px-6 py-4">
                  <div className="h-4 bg-gray-100 rounded animate-pulse" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </TableWrapper>
    );
  }

  if (!isLoading && (!data || data.length === 0)) {
    return (
      <TableWrapper>
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, i) => (
              <th
                key={i}
                className={`px-6 py-3 text-${
                  column.align || "left"
                } text-xs font-medium text-gray-500 uppercase tracking-wider`}
              >
                {t(column.header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={columns.length}>
              <EmptyState />
            </td>
          </tr>
        </tbody>
      </TableWrapper>
    );
  }

  return (
    <TableWrapper>
      <thead className="bg-gray-50">
        <tr>
          {columns.map((column, i) => (
            <th
              key={i}
              className={`px-6 py-3 text-${
                column.align || "left"
              } text-xs font-medium text-gray-500 uppercase tracking-wider ${
                column.className || ""
              } ${column.divider ? DIVIDER_CLASS : ""}`}
              onClick={() => column.sortKey && requestSort(column.sortKey)}
            >
              <div className="flex items-center space-x-1 justify-between">
                <span>{t(column.header)}</span>
                {column.sortKey && (
                  <div className="flex flex-col">
                    <ChevronUpIcon
                      className={`h-3 w-3 ${
                        sortConfig.key === column.sortKey &&
                        sortConfig.direction === "asc"
                          ? "text-gray-900"
                          : "text-gray-400"
                      }`}
                    />
                    <ChevronDownIcon
                      className={`h-3 w-3 ${
                        sortConfig.key === column.sortKey &&
                        sortConfig.direction === "desc"
                          ? "text-gray-900"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {sortedData.map((row, rowIndex) => (
          <tr
            key={rowIndex}
            className={[
              typeof rowClassName === "function"
                ? rowClassName(row, rowIndex)
                : "",
              onRowClick ? "cursor-pointer hover:bg-gray-50" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
          >
            {columns.map((column, colIndex) => (
              <td
                key={colIndex}
                className={`px-6 py-4 whitespace-nowrap text-sm text-${
                  column.align || "left"
                } ${column.className || "text-gray-900"} ${
                  column.divider ? DIVIDER_CLASS : ""
                }`}
              >
                {typeof column.accessor === "function"
                  ? column.accessor(row, rowIndex)
                  : row[column.accessor]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {footer && (
        <tfoot className="bg-gray-50 border-t border-gray-200">
          <tr>
            {columns.map((column, colIndex) => (
              <td
                key={colIndex}
                className={`px-6 py-3 whitespace-nowrap text-sm font-semibold text-${
                  column.align || "left"
                } ${column.className || "text-gray-900"} ${
                  column.divider ? DIVIDER_CLASS : ""
                }`}
              >
                {footer[colIndex] ?? null}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </TableWrapper>
  );
};

export default Table;
